import { toast } from "@/v2-ui/toast"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import { useLicenseAddOnRevokeMutation } from "@/v2-backoffice-client/license/addOns/license.addOns.revoke.mutation"
import { evictLicenseAddOnQueries } from "@/v2-backoffice-client/license/license.utils"
import type { AddOnGrantsQuery } from "@/v2-backoffice-client/license/addOns/catalog/view/__types__/license.addOn.grants.query"

export type LicenseAddOnGrantsRowToolbarProps = {
  addOnId: number
  addOnName: string
  grant: AddOnGrantsQuery["getAddOnGrants"][number]
}

// The one action a row supports: removing the add-on from that customer —
// same write as the customer panel's Remove button, offered here because
// "take it off these three customers" is naturally worked from the add-on's
// side.
function LicenseAddOnGrantsRowToolbar(
  props: LicenseAddOnGrantsRowToolbarProps,
) {
  const { addOnId, addOnName, grant } = props

  const [revokeAddOn, { loading, error }] = useLicenseAddOnRevokeMutation({
    update: evictLicenseAddOnQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Add-on removed",
        description: `${grant.CustomerName ?? "The customer"} no longer has ${addOnName}.`,
      })
    },
  })

  return (
    <ActionMenu>
      <ActionMenu.Trigger />
      <ActionMenu.Content>
        <ActionMenu.ConfirmItem
          iconSrc="trash"
          variant="destructive"
          isLoading={loading}
          error={error}
          errorTitle="Failed to remove add-on"
          confirmTitle="Remove Add-on"
          confirmDescription={`${grant.CustomerName ?? "This customer"} loses whatever ${addOnName} unlocks the moment entitlements next resolve. Their licenses are untouched.`}
          confirmText="Remove"
          confirmVariant="destructive"
          data-feature-code="UI_BackofficeAddOn_Grants_Revoke"
          onConfirm={async () => {
            await revokeAddOn({
              variables: { customerId: grant.CustomerId, addOnId },
            })
          }}
        >
          Remove
        </ActionMenu.ConfirmItem>
      </ActionMenu.Content>
    </ActionMenu>
  )
}

export default LicenseAddOnGrantsRowToolbar
